import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";

type Tone = "neutral" | "pos" | "warn" | "neg";
type Toast = { id: number; message: ReactNode; tone: Tone };

const ToastCtx = createContext<(message: ReactNode, tone?: Tone) => void>(() => {});

/** Wrap the app once. Then call useToast() anywhere. */
export function ToastProvider({ children, duration = 5000 }: {
  children: ReactNode; duration?: number;
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const push = useCallback((message: ReactNode, tone: Tone = "neutral") => {
    // Counter, not Date.now(), so two toasts in the same millisecond stay distinct.
    setToasts((t) => {
      const id = (t.length ? Math.max(...t.map((x) => x.id)) : 0) + 1;
      setTimeout(() => dismiss(id), duration);
      return [...t, { id, message, tone }];
    });
  }, [dismiss, duration]);

  const value = useMemo(() => push, [push]);

  return (
    <ToastCtx.Provider value={value}>
      {children}
      {/* aria-live so screen readers announce without stealing focus. */}
      <div className="toast-region" role="region" aria-live="polite" aria-label="Notifications">
        {toasts.map((t) => (
          <div key={t.id} className={["toast", t.tone !== "neutral" && `toast-${t.tone}`]
                 .filter(Boolean).join(" ")}>
            <span style={{ flex: 1, minWidth: 0 }}>{t.message}</span>
            <button className="toast-close" onClick={() => dismiss(t.id)} aria-label="Dismiss">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);
